import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ArticleService } from '../../../service/article.service';
import { CatalogoService } from '../../../service/catalogo.service';
import { Article } from '../../../models/Article';
import { GeneralService } from '../../../service/general.service';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { SourceArticleAvailableUnitOption } from '../../../features/source-articles/models/source-article.model';
import { SourceArticlesService } from '../../../features/source-articles/services/source-articles.service';

interface ArticleUnitOption {
  pk: number;
  descripcion: string;
  factorConversion: number;
}

@Component({
  selector: 'app-add-article',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
  templateUrl: './add-article.html',
  styleUrls: ['./add-article.scss'],
})
export class AddArticle {

  private fb = inject(FormBuilder);
  private articleService = inject(ArticleService);
  private catalogService = inject(CatalogoService);
  private generalService = inject(GeneralService);
  private sourceArticlesService = inject(SourceArticlesService);


  loading = signal(false);
  success = signal(false);
  error = signal<string | null>(null);
  variedades = signal<any[]>([]);
  unidadesMedida = signal<any[]>([]);
  unidadesMedidaDisponibles = signal<ArticleUnitOption[]>([]);
  nombreSeccion = signal('');
  nombreDivision = signal('');
  nombreClase = signal('');
  nombreProducto = signal('');
  readonly yesNoOptions = [
    { value: 'S', label: 'Sí' },
    { value: 'N', label: 'No' }
  ];
  readonly statusOptions = [
    { value: 'A', label: 'Activo' },
    { value: 'I', label: 'Inactivo' },
    { value: 'P', label: 'Pendiente' }
  ];

  constructor(
    private router: Router,
    // otros injections
  ) { }


  form = this.fb.group({
    codigoIne: ['', Validators.required],
    nombre: ['', Validators.required],

    fkVariedad: [0, Validators.required],
    fkSeccion: [{ value: 0, disabled: true }],
    fkDivision: [{ value: 0, disabled: true }],
    fkClase: [{ value: 0, disabled: true }],
    fkProducto: [{ value: 0, disabled: true }],
    fkUnidadMedida: [0, Validators.required],

    factorConversion: [1, [Validators.required, Validators.min(0.0000001)]],
    calculo: ['', Validators.required],
    calculoAlterno: ['', Validators.required],
    cotizarEnDolares: ['N' as 'S' | 'N', Validators.required],
    codigoCiiu: [''],
    codigoNaeg: [''],
    codigoNpg: [''],
    codigoCcp: [''],
    referencia: [''],
    estado: ['A']
  });
  
  ngOnInit(): void {
    this.catalogService.getVariedades(0)
      .subscribe(res => this.variedades.set(res));
    this.generalService
      .get('GET_UNIDADES_MEDIDA')
      .subscribe(res => this.unidadesMedida.set(res ?? []));
  }

  private getAuthUserPk(): number {
    const u = localStorage.getItem('authUser');
    return u ? JSON.parse(u).pkUsuario : 0;
  }

  async onVariedadChange(): Promise<void> {
    const fkVariedad = Number(this.form.value.fkVariedad);
    const variedad = this.variedades().find(v => v.codigo === fkVariedad);
    if (!variedad) return;

    if (!variedad.fkSeccion) {
      this.nombreSeccion.set('');
      this.nombreDivision.set('');
      this.nombreClase.set('');
      this.nombreProducto.set('');
      this.unidadesMedidaDisponibles.set([]);
      return;
    }

    const baseUnitMeasureId = this.resolveVarietyUnitMeasureId(variedad);

    this.form.patchValue({
      fkSeccion: Number(variedad.fkSeccion ?? 0),
      fkDivision: Number(variedad.fkDivision ?? 0),
      fkClase: Number(variedad.fkClase ?? 0),
      fkProducto: Number(variedad.fkProducto ?? 0),
      fkUnidadMedida: baseUnitMeasureId,
      factorConversion: 1
    });

    this.loadAvailableUnitMeasureOptions(baseUnitMeasureId);

    const secciones = (await this.catalogService.getSecciones().toPromise()) ?? [];
    this.nombreSeccion.set(
      secciones.find(s => s.codigo === variedad.fkSeccion)?.nombre ?? ''
    );

    const divisiones =
      (await this.catalogService.getDivisiones(variedad.fkSeccion).toPromise()) ?? [];
    this.nombreDivision.set(
      divisiones.find(d => d.codigo === variedad.fkDivision)?.nombre ?? ''
    );

    const clases =
      (await this.catalogService.getClases(variedad.fkDivision).toPromise()) ?? [];
    this.nombreClase.set(
      clases.find(c => c.codigo === variedad.fkClase)?.nombre ?? ''
    );

    const productos =
      (await this.catalogService.getProductos(variedad.fkClase).toPromise()) ?? [];
    this.nombreProducto.set(
      productos.find(p => p.codigo === variedad.fkProducto)?.nombre ?? ''
    );
  }

  onUnidadMedidaChange(): void {
    const selectedUnitMeasureId = this.resolveSelectedUnitMeasureId();
    const selectedUnit = this.unidadesMedidaDisponibles().find(
      item => Number(item.pk) === selectedUnitMeasureId
    );

    this.form.patchValue({
      factorConversion: selectedUnit?.factorConversion ?? 1
    });
  }




  onSubmit(): void {
    if (this.form.invalid) return;

    this.loading.set(true);

    const raw = this.form.getRawValue();

    const payload: Article = {
      codigoIne: raw.codigoIne ?? '',
      nombre: raw.nombre ?? '',

      fkVariedad: raw.fkVariedad ?? 0,
      fkSeccion: raw.fkSeccion ?? 0,
      fkDivision: raw.fkDivision ?? 0,
      fkClase: raw.fkClase ?? 0,
      fkProducto: raw.fkProducto ?? 0,

      codigoCiiu: raw.codigoCiiu ?? '',
      codigoNaeg: raw.codigoNaeg ?? '',
      codigoNpg: raw.codigoNpg ?? '',
      codigoCcp: raw.codigoCcp ?? '',
      referencia: raw.referencia ?? '',
      estado: (raw.estado ?? 'A') as 'A',

      segFuncion: 'ADD',
      segUsuario: this.getAuthUserPk(),
      segTransaccion: 5,
      segDatosAnteriores: '',
      fkUnidadMedida: raw.fkUnidadMedida ?? 1,
      factorConversion: raw.factorConversion ?? 1,
      calculo: (raw.calculo ?? 'S') as 'S' | 'N',
      calculoAlterno: (raw.calculoAlterno ?? 'N') as 'S' | 'N',
      cotizarEnDolares: (raw.cotizarEnDolares ?? 'N') as 'S' | 'N',


    };


    this.articleService.add(payload).subscribe({
      next: () => {
        this.success.set(true);
        this.form.reset({ estado: 'A' });
        this.loading.set(false);
        this.nombreSeccion.set('');
        this.nombreDivision.set('');
        this.nombreClase.set('');
        this.nombreProducto.set('');
        this.unidadesMedidaDisponibles.set([]);
        setTimeout(() => {
          this.success.set(false);
        }, 3000);
      },
      error: () => {
        this.error.set('Error al guardar artículo');
        this.loading.set(false);
      }
    });
  }
  onCancel(): void {
    this.router.navigate(['/home']);
  }

  getSelectedUnitMeasureOptions(): ArticleUnitOption[] {
    const unitMeasureId = this.resolveSelectedUnitMeasureId();
    const availableUnits = this.unidadesMedidaDisponibles();

    if (availableUnits.length > 0) {
      return availableUnits;
    }

    if (unitMeasureId === null) {
      return [];
    }

    return [this.buildUnitMeasureOption(unitMeasureId, 1)];
  }

  private loadAvailableUnitMeasureOptions(baseUnitMeasureId: number): void {
    const fallbackOption = this.buildUnitMeasureOption(baseUnitMeasureId, 1);

    this.unidadesMedidaDisponibles.set([fallbackOption]);

    this.sourceArticlesService.getAvailableUnitsByBaseUnit(baseUnitMeasureId).subscribe({
      next: (response) => {
        const options = this.normalizeAvailableUnitOptions(response.data, fallbackOption);
        this.unidadesMedidaDisponibles.set(options);

        const selectedUnitMeasureId = this.resolveSelectedUnitMeasureId();
        if (!options.some(item => item.pk === selectedUnitMeasureId)) {
          this.form.patchValue({ fkUnidadMedida: baseUnitMeasureId });
        }

        this.onUnidadMedidaChange();
      },
      error: () => {
        this.unidadesMedidaDisponibles.set([fallbackOption]);
        this.form.patchValue({ fkUnidadMedida: baseUnitMeasureId, factorConversion: 1 });
      }
    });
  }

  private normalizeAvailableUnitOptions(
    options: SourceArticleAvailableUnitOption[] | null | undefined,
    fallbackOption: ArticleUnitOption
  ): ArticleUnitOption[] {
    const normalizedOptions = (options ?? [])
      .map(item => {
        const unitMeasureId = Number(item?.unitMeasureId);
        if (!Number.isFinite(unitMeasureId) || unitMeasureId < 0) {
          return null;
        }

        return {
          pk: Math.trunc(unitMeasureId),
          descripcion: `${item?.unitMeasureName ?? ''}`.trim() || `#${Math.trunc(unitMeasureId)}`,
          factorConversion: this.resolvePositiveNumber(item?.conversionFactor, 1)
        };
      })
      .filter((item): item is ArticleUnitOption => item !== null);

    if (!normalizedOptions.some(item => item.pk === fallbackOption.pk)) {
      normalizedOptions.unshift(fallbackOption);
    }

    return normalizedOptions.length > 0 ? normalizedOptions : [fallbackOption];
  }

  private buildUnitMeasureOption(unitMeasureId: number, factorConversion: number): ArticleUnitOption {
    const unit = this.unidadesMedida().find(item => Number(item?.pk) === unitMeasureId);
    return {
      pk: unitMeasureId,
      descripcion: unit?.descripcion ?? (unitMeasureId === 0 ? 'Sin unidad de medida' : `#${unitMeasureId}`),
      factorConversion
    };
  }

  private resolveVarietyUnitMeasureId(variedad: any): number {
    const rawValue =
      variedad?.fkUnidadMedida ??
      variedad?.measurementUnitId ??
      variedad?.fkUnidadmedida ??
      variedad?.fk_unidad_medida ??
      0;

    const unitMeasureId = Number(rawValue);
    return Number.isFinite(unitMeasureId) && unitMeasureId >= 0 ? Math.trunc(unitMeasureId) : 0;
  }

  private resolveSelectedUnitMeasureId(): number | null {
    const rawValue = this.form.value.fkUnidadMedida;
    if (rawValue === null || rawValue === undefined) {
      return null;
    }

    const unitMeasureId = Number(rawValue);
    return Number.isFinite(unitMeasureId) && unitMeasureId >= 0 ? Math.trunc(unitMeasureId) : null;
  }

  private resolvePositiveNumber(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

}
